'use client';

import { useTheme } from '@mui/material/styles';
import {
	DataGridPremium,
	GridPaginationModel,
	GridRowSelectionModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { useQuery } from '@parkchirp/api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { LIST_ALL_RESERVED_SPACES, LIST_ALL_RESERVED_SPACES_CSV } from '../../queries';
import { datagridFilterPanelAttributes, dayjs } from '../../utils';
import { Snackbar } from '../alerts';
import { DatagridLinearProgress } from '../loading';
import { Dialog } from '../modals';
import { CsvExportButtonV2 } from '../reports';
import {
	getParkerReservedSpacesTableColumns,
	OnFilterModelChange,
	OnSortModelChange,
} from './reservedSpacesTableConfig';

const defaultSortOrder = {
	field: 'createdAt',
	sort: 'asc',
};

export const ReservedSpacesTable = ({
	setShowSnackbar,
	setShowDialog,
	showDialog = null,
	showSnackbar = null,
	theme,
}) => {
	const OwnerPortalTheme = useTheme();
	const [rowCount, setRowCount] = useState<number>(0);
	const [sortOrder, setSortOrder] = useState<{ field: string; sort: string }>(defaultSortOrder);
	const [filters, setFilters] = useState([]);
	const debouncedSetFilters = useDebounce(setFilters, 500);
	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [selectedParkerIds, setSelectedParkerIds] = useState<GridRowSelectionModel>([]);
	const [showSnackbarForParkersTable, setShowSnackbarForParkersTable = setShowSnackbar] = useState(showSnackbar);
	const [showDialogForParkersTable, setShowDialogForParkersTable = setShowDialog] = useState(showDialog);

	const {
		data: allParkerReservedSpaces,
		loading: allParkerReservedSpacesLoading,
		refetch: refetchAllParkerReservedSpacesFromCache,
	} = useQuery(LIST_ALL_RESERVED_SPACES, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			order: sortOrder,
			filters,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
	});

	const parkerRows = useMemo(
		() => allParkerReservedSpaces?.listAllReservedSpaces.reservedSpaces ?? [],
		[allParkerReservedSpaces?.listAllReservedSpaces.reservedSpaces],
	);

	useEffect(() => {
		if (!allParkerReservedSpacesLoading && allParkerReservedSpaces) {
			setRowCount(allParkerReservedSpaces?.listAllReservedSpaces?.count);
		}
	}, [allParkerReservedSpacesLoading, allParkerReservedSpaces]);

	useEffect(() => {
		setRowCount((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
	}, [rowCount]);

	const columns = getParkerReservedSpacesTableColumns({
		setShowDialog,
		refetchAllParkerReservedSpacesFromCache,
		theme: theme || OwnerPortalTheme,
		setShowSnackbar: setShowSnackbarForParkersTable,
	});

	const ReservedSpacesTableToolbar = () => (
		<GridToolbarContainer>
			<GridToolbarFilterButton data-cy='filter-table-button' />
			<GridToolbarColumnsButton />
			<GridToolbarDensitySelector />
			<CsvExportButtonV2
				query={LIST_ALL_RESERVED_SPACES_CSV}
				queryName='listAllReservedSpacesCSV'
				args={{
					variables: {
						order: sortOrder,
						filters,
					},
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
				}}
			/>
		</GridToolbarContainer>
	);

	const onSortModelChange = useCallback((sortModel) => {
		OnSortModelChange({ setPaginationModel, setSortOrder })(sortModel);
	}, []);

	const onFilterModelChange = useCallback(
		(filterModel) => {
			OnFilterModelChange({ setFilters: debouncedSetFilters, setPaginationModel })(filterModel);
		},
		[debouncedSetFilters],
	);

	return (
		<div className='pca__datagrid-sticky--headfoot' data-cy='parkers-table-container'>
			<DataGridPremium
				getRowId={(row) => row.id}
				paginationModel={{ page, pageSize }}
				onSortModelChange={onSortModelChange}
				onFilterModelChange={onFilterModelChange}
				hideFooter={allParkerReservedSpacesLoading}
				onPaginationModelChange={setPaginationModel}
				pagination
				rows={parkerRows || []}
				rowCount={rowCount}
				pageSizeOptions={[pageSize]}
				columns={columns}
				paginationMode='server'
				sortingMode='server'
				filterMode='server'
				loading={allParkerReservedSpacesLoading}
				slots={{
					toolbar: ReservedSpacesTableToolbar,
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{
					filterPanel: { ...datagridFilterPanelAttributes },
				}}
				rowSelectionModel={selectedParkerIds}
				onRowSelectionModelChange={(ids) => setSelectedParkerIds(ids)}
				initialState={{
					filter: {
						filterModel: {
							items: [],
						},
					},
				}}
				sx={{
					'& .red-row': {
						backgroundColor: 'rgba(255, 99, 71, .9) !important',
					},
					height: '100%',
				}}
			/>
			{!showSnackbar && (
				<Snackbar showSnackbar={showSnackbarForParkersTable} setShowSnackbar={setShowSnackbarForParkersTable} />
			)}
			{!showDialog && (
				<Dialog showDialog={showDialogForParkersTable} setShowDialog={setShowDialogForParkersTable} />
			)}
		</div>
	);
};
